import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Quill } from "react-quill";
import Input from "../../../../../../common/element/Input/Input";
import BtnPrimary from "../../../../../../common/element/Buttons/btnPrimary";
import BtnLight from "../../../../../../common/element/Buttons/btnLight";
import Dialog from "../../../../../../common/element/Dialog/Dialog";
import RichTextEditor from "../../../../../../common/snippet/RichTextEditor";
import ThreeDotBouncing from "../../../../../../common/element/PageLoader";
import { notify } from "react-notify-toast";
import {
  GetNotificationEmailTemplateById,
  UpdateNotificationEmailTemplate,
  SendTestNotificationEmail,
} from "../../../../../../actions/Admin";

export const ALL_SYSTEM_VARIABLES = [
  "APP_URL",
  "_clientTradingNames",
  "_message",
  "_otp",
  "abn",
  "abn_nzbn",
  "accept_invite_link",
  "accept_url",
  "cardlast4",
  "charged_amount",
  "client_assigned_list",
  "client_contact_name",
  "client_name",
  "client_note",
  "client_rejected_note",
  "client_trading_name",
  "compliance_checklist_link",
  "continue_signup_link",
  "date",
  "docUrl",
  "doc_name",
  "document_name",
  "email_to",
  "expiry_date",
  "failure_reason",
  "first_name",
  "invitation_link",
  "inviteByUserName",
  "invite_link",
  "invite_message",
  "inviter_first_name",
  "inviter_name",
  "invoice_number",
  "last_name",
  "license_period_end",
  "license_period_start",
  "name",
  "name_of_compliance_checklist",
  "next_renewal_date",
  "organisation_id",
  "password_reset_link",
  "providerName",
  "provider_business_name",
  "provider_contact_name",
  "provider_id",
  "provider_name",
  "provider_org_name",
  "provider_trading_name",
  "provider_user_name",
  "recipient",
  "recipient_first_name",
  "reject_invite_message",
  "reject_link",
  "reject_url",
  "renewal_date",
  "send_to_email",
  "service_email",
  "sub_created_date",
  "support_email",
  "tax_rate",
  "total_gst_amount",
  "total_including_gst",
  "trading_name",
  "update_card_url",
  "url",
  "user_name",
  "user_otp",
  "verification_link",
  "verify_otp_link",
  "worker_first_name",
  "worker_name",
  "worker_profile_link",
  "workers"
];

export const convertRawTagsToBadges = (html) => {
  if (!html) return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
    const container = doc.body.firstElementChild;
    if (!container) return html;

    // Traverse all text nodes only (never touch attributes like href="mailto:{{support_email}}")
    const walk = doc.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    const textNodes = [];
    let node;
    while ((node = walk.nextNode())) {
      if (node.nodeValue && /\{\{[a-zA-Z0-9_]+\}\}/.test(node.nodeValue)) {
        textNodes.push(node);
      }
    }

    textNodes.forEach((textNode) => {
      const parent = textNode.parentNode;
      if (!parent) return;

      const parts = textNode.nodeValue.split(/(\{\{[a-zA-Z0-9_]+\}\})/g);
      const frag = doc.createDocumentFragment();

      parts.forEach((part) => {
        const match = part.match(/^\{\{([a-zA-Z0-9_]+)\}\}$/);
        if (match) {
          const varName = match[1];
          const span = doc.createElement("span");
          span.className = "email-variable-badge";
          span.setAttribute("data-variable", varName);
          span.setAttribute("contenteditable", "false");
          span.textContent = `{{${varName}}}`;
          frag.appendChild(span);
        } else if (part) {
          frag.appendChild(doc.createTextNode(part));
        }
      });

      parent.replaceChild(frag, textNode);
    });

    return container.innerHTML;
  } catch (e) {
    console.error("convertRawTagsToBadges error:", e);
    return html;
  }
};

export const convertBadgesToRawTags = (html) => {
  if (!html) return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
    const badges = doc.querySelectorAll("span[data-variable], span.email-variable-badge");
    badges.forEach((el) => {
      const varName = el.getAttribute("data-variable") || el.textContent.replace(/[{}]/g, "").trim();
      const textNode = doc.createTextNode(`{{${varName}}}`);
      el.parentNode.replaceChild(textNode, el);
    });
    return (doc.body.firstElementChild?.innerHTML || html).replace(/\uFEFF/g, "");
  } catch (e) {
    return html.replace(/<span[^>]*data-variable="([^"]+)"[^>]*>[\s\S]*?<\/span>/gi, "{{$1}}");
  }
};

const sampleReplacements = {
  APP_URL: window.location.origin,
  support_email: "support@conserve.com.au",
  service_email: "service@conserve.com.au",
  first_name: "Alex",
  last_name: "Example",
  user_name: "Alex Example",
  name: "Alex Example",
  recipient: "Alex Example",
  recipient_first_name: "Alex",
  inviter_first_name: "Sarah",
  inviter_name: "Sarah Jenkins",
  inviteByUserName: "Sarah Jenkins",
  client_name: "Acme Corporation",
  client_trading_name: "Acme Corporation",
  provider_name: "Apex Services Pty Ltd",
  provider_business_name: "Apex Services Pty Ltd",
  provider_trading_name: "Apex Services Pty Ltd",
  trading_name: "Apex Services Pty Ltd",
  provider_contact_name: "Alex Example",
  provider_user_name: "Alex Example",
  abn: "12 345 678 901",
  abn_nzbn: "12 345 678 901",
  provider_id: "PRV-100234",
  organisation_id: "PRV-100234",
  worker_name: "Alex Example",
  worker_first_name: "Alex",
  worker_profile_link: "#",
  document_name: "Public Liability Insurance",
  doc_name: "Public Liability Insurance",
  expiry_date: "30 Nov 2026",
  renewal_date: "31 Dec 2026",
  next_renewal_date: "31 Dec 2026",
  name_of_compliance_checklist: "General Contractor Checklist",
  invoice_number: "INV-2026-0891",
  date: "23 Oct 2026",
  total_including_gst: "$1,250.00",
  verification_link: "#",
  continue_signup_link: "#",
  _otp: "123456",
  verify_otp_link: "#",
  password_reset_link: "#",
  invite_link: "#",
  reject_link: "#",
  accept_url: "#",
  reject_url: "#",
  accept_invite_link: "#",
  update_card_url: "#",
  failure_reason: "Card expired or declined by issuer",
  client_note: "Please provide updated certificate of currency.",
  client_rejected_note: "Please provide updated certificate of currency.",
  workers: "David Smith, Sarah Connor",
  _clientTradingNames: "Acme Corporation",
  email_to: "alex@example.com",
  invitation_link: "#",
  invite_message: "You have been invited to join the Conserve compliance portal.",
  reject_invite_message: "Invitation has been declined.",
  license_period_start: "01 Jan 2026",
  license_period_end: "31 Dec 2026",
  _message: "Document approval notification.",
  client_contact_name: "Sarah Jenkins",
  provider_org_name: "Apex Services Pty Ltd",
  providerName: "Apex Services Pty Ltd",
  docUrl: "#",
  compliance_checklist_link: "#",
  sub_created_date: "23 Oct 2026",
  total_gst_amount: "$125.00",
  tax_rate: "10%",
  charged_amount: "$1,250.00",
  cardlast4: "4242",
  user_otp: "123456",
  client_assigned_list: "Acme Corporation",
  send_to_email: "alex@example.com",
  url: "#"
};

const compileSampleString = (str) => {
  if (!str) return "";
  let rendered = str;
  Object.keys(sampleReplacements).forEach((key) => {
    const val = sampleReplacements[key];
    const regex = new RegExp(`{{${key}}}`, "g");
    rendered = rendered.replace(regex, val);
  });
  return rendered;
};

const wrapWithConserveEmailLayout = (bodyHtml, baseUrl = "{{APP_URL}}") => {
  const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  let formattedBody = bodyHtml || "";
  if (/\{\{#each\s+_otp\}\}/i.test(formattedBody)) {
    formattedBody = formattedBody.replace(
      /(?:<p[^>]*>)?\s*\{\{#each\s+_otp\}\}[\s\S]*?\{\{\/each\}\}\s*(?:<\/p>)?/gi,
      `<p style="margin: 16px 0 20px 0;">{{#each _otp}}<span style="display:inline-block;border:2px solid #5786cc;width:64px;height:64px;text-align:center;line-height:60px;font-weight:700;border-radius:8px;font-size:48px;color:#5786cc;margin-right:6px;margin-bottom:8px;">{{this}}</span>{{/each}}</p>`
    );
  }
  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
   <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
   <meta name="viewport" content="width=device-width, initial-scale=1" />
   <title>Conserve</title>
   <link href="https://fonts.googleapis.com/css2?family=Raleway:wght@200;300;400;500;600;700;800;900&display=swap" rel="stylesheet">
   <style type="text/css">
      body, img, div, p, ul, li, span, strong, a {
         margin: 0;
         padding: 0;
         line-height: 100%;
      }
      table td {
         border-collapse: collapse;
         vertical-align: top;
      }
      body, #body_style {
         width: 100% !important;
         color: #292c30;
         font-size: 14px;
         font-family: 'Raleway', sans-serif !important;
         line-height: 20px;
         background-color: #f5faff;
         -webkit-text-size-adjust: none;
         -ms-text-size-adjust: none;
         margin: 0;
         padding: 0;
      }
      p {
         margin: 0 0 16px 0 !important;
         line-height: 1.6 !important;
         color: #344054 !important;
         font-family: 'Raleway', sans-serif !important;
         font-size: 14px !important;
      }
      p:empty {
         min-height: 14px !important;
         margin-bottom: 14px !important;
      }
      a {
         color: #5786cc;
         text-decoration: none;
      }
      img {
         border: none;
         outline: none;
         text-decoration: none;
         height: auto;
         max-width: 100%;
      }
      table {
         border-spacing: 0;
         border-collapse: collapse;
      }
   </style>
</head>
<body style="width:100% !important; color:#292c30; background:#f5faff; font-family: 'Raleway', sans-serif; font-size:14px; line-height:20px; margin:0; padding:0;">
   <table width="576" border="0" cellspacing="0" cellpadding="0" align="center" class="table_wrapper" style="width: 576px; max-width: 576px; background:#f5faff;">
      <tr>
         <td height="32" style="height:32px; font-size:1px; line-height:32px;">&nbsp;</td>
      </tr>
      <!-- White Content Card -->
      <tr>
         <td>
            <table border="0" cellspacing="0" cellpadding="0" width="100%">
               <tr>
                  <td style="padding: 24px 24px 28px 24px; background:#ffffff;">
                     <table border="0" cellspacing="0" cellpadding="0" width="100%">
                        <!-- Header Logo -->
                        <tr>
                           <td align="left">
                              <a href="#" target="_blank"><img src="${cleanBaseUrl}/email/logo.png" width="149" height="30" alt="Conserve" style="display:block; border:0;" /></a>
                           </td>
                        </tr>
                        <tr>
                           <td height="40" style="height:40px; font-size:1px; line-height:40px;">&nbsp;</td>
                        </tr>
                        <!-- Editable Body Content -->
                        <tr>
                           <td style="font-family: 'Raleway', sans-serif; font-size: 14px; line-height: 1.6; color: #344054;">
${formattedBody}
                           </td>
                        </tr>
                     </table>
                  </td>
               </tr>
            </table>
         </td>
      </tr>
      <!-- Canonical Conserve Footer Section -->
      <tr>
         <td style="background: #f5faff; padding: 24px 20px 32px 20px;" id="footertop">
            <table border="0" cellspacing="0" cellpadding="0" align="center" width="100%">
               <tr>
                  <td style="color:#475467; font-family: 'Raleway', sans-serif; font-size: 12px; line-height: 20px; text-align: left; font-weight: 400;">
                     This email was sent to <a href="mailto:{{send_to_email}}" style="color:#5786cc; text-decoration: none;"><strong>{{send_to_email}}</strong></a>. You are receiving this email because you have registered to be part of the Conserve community, are a current user, or are a contact we felt may be interested to keep up to date on developments.
                  </td>
               </tr>
               <tr>
                  <td height="20" style="height: 20px; font-size: 1px; line-height: 20px;">&nbsp;</td>
               </tr>
               <tr>
                  <td style="color:#475467; font-family: 'Raleway', sans-serif; font-size: 12px; line-height: 20px; text-align: left; font-weight: 400;">
                     If you'd rather not receive this kind of email, you can <a href="#" style="color:#5786cc; text-decoration: none;"><strong>unsubscribe</strong></a> or <a href="#" style="color:#5786cc; text-decoration: none;"><strong>manage your email preferences</strong>.</a>
                  </td>
               </tr>
               <tr>
                  <td height="20" style="height: 20px; font-size: 1px; line-height: 20px;">&nbsp;</td>
               </tr>
               <tr>
                  <td style="color:#475467; font-family: 'Raleway', sans-serif; font-size: 12px; line-height: 20px; text-align: left; font-weight: 400;">
                     ©Copyright (C) 2022 Conserve. All rights reserved.<br /> 5 Celebration Drive, Bella Vista NSW 2153, Australia
                  </td>
               </tr>
               <tr>
                  <td height="32" style="height: 32px; font-size: 1px; line-height: 32px;">&nbsp;</td>
               </tr>
               <tr>
                  <td>
                     <table width="100%" cellspacing="0" cellpadding="0" border="0" align="center">
                        <tr>
                           <td align="left">
                              <a href="#" target="_blank"><img src="${cleanBaseUrl}/email/logo.png" width="112" height="22" alt="Conserve" style="display:block; border:0;" /></a>
                           </td>
                           <td align="right" style="text-align:right">
                              <table width="120" cellspacing="0" cellpadding="0" border="0" align="right">
                                 <tr>
                                    <td><a href="#"><img src="${cleanBaseUrl}/email/twitter.png" alt="" width="20" height="20" style="display:block; border:0;" /></a></td>
                                    <td style="padding-left: 8px;"><a href="#"><img src="${cleanBaseUrl}/email/facebook.png" alt="" width="20" height="20" style="display:block; border:0;" /></a></td>
                                    <td style="padding-left: 8px;"><a href="#"><img src="${cleanBaseUrl}/email/instagram.png" alt="" width="20" height="20" style="display:block; border:0;" /></a></td>
                                 </tr>
                              </table>
                           </td>
                        </tr>
                     </table>
                  </td>
               </tr>
               <tr>
                  <td height="32" style="height: 32px; font-size: 1px; line-height: 32px;">&nbsp;</td>
               </tr>
            </table>
         </td>
      </tr>
   </table>
</body>
</html>`;
};

const extractEditableBody = (html) => {
  if (!html) return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // 1. Remove footertop and its row if present
    const footer = doc.getElementById("footertop");
    if (footer) {
      const parentRow = footer.closest("tr");
      if (parentRow) parentRow.remove();
      else footer.remove();
    }

    // 2. Remove any top logo elements (width 149, logo.png) and spacer rows
    const allImgs = Array.from(doc.querySelectorAll("img"));
    for (const img of allImgs) {
      const src = img.getAttribute("src") || "";
      if (
        src.includes("logo.png") ||
        src.includes("twitter.png") ||
        src.includes("facebook.png") ||
        src.includes("instagram.png")
      ) {
        const row = img.closest("tr");
        if (row && (row.textContent.includes("©") || !row.textContent.trim())) {
          row.remove();
        } else {
          img.remove();
        }
      } else if (src.includes("spacer.gif") || src.includes("spacer")) {
        const row = img.closest("tr");
        if (row && !row.textContent.trim()) row.remove();
      }
    }

    const isFooterNoise = (text) => {
      if (!text) return false;
      const lower = text.toLowerCase();
      return (
        lower.includes("this email was sent to") ||
        lower.includes("©copyright") ||
        lower.includes("all rights reserved") ||
        lower.includes("celebration drive") ||
        lower.includes("if you'd rather not receive") ||
        lower.includes("unsubscribe") ||
        lower.includes("manage your email preferences")
      );
    };

    // 3. Collect leaf TDs or paragraphs
    let rawParagraphs = [];
    const leafTds = Array.from(doc.querySelectorAll("td")).filter(
      (td) => !td.querySelector("table")
    );

    if (leafTds.length > 0) {
      for (const td of leafTds) {
        td.querySelectorAll('img[src*="spacer"]').forEach((i) => i.remove());
        const ps = Array.from(td.querySelectorAll("p"));
        if (ps.length > 0) {
          ps.forEach((p) => {
            const content = p.innerHTML.trim();
            const text = p.textContent.trim();
            if (content && !content.includes("logo.png") && !isFooterNoise(text)) {
              rawParagraphs.push(content);
            }
          });
        } else {
          const content = td.innerHTML.trim();
          const text = td.textContent.trim();
          if (content && !content.includes("logo.png") && !isFooterNoise(text)) {
            rawParagraphs.push(content);
          }
        }
      }
    } else {
      const allPs = Array.from(doc.querySelectorAll("p"));
      for (const p of allPs) {
        const content = p.innerHTML.trim();
        const text = p.textContent.trim();
        if (content && !isFooterNoise(text)) {
          rawParagraphs.push(content);
        }
      }
    }

    // 4. Combine Ph: and Email: if separated across two consecutive paragraphs
    const cleanedParagraphs = [];
    for (let i = 0; i < rawParagraphs.length; i++) {
      const curr = rawParagraphs[i];
      const next = rawParagraphs[i + 1];
      if (
        curr.includes("Ph:") &&
        !curr.includes("Email:") &&
        next &&
        next.includes("Email:")
      ) {
        cleanedParagraphs.push(`${curr}<br />${next}`);
        i++; // skip next
      } else {
        cleanedParagraphs.push(curr);
      }
    }

    if (cleanedParagraphs.length > 0) {
      return cleanedParagraphs.map((p) => `<p>${p}</p>`).join("\n");
    }
  } catch (e) {
    console.warn("extractEditableBody parsing failed:", e);
  }
  return html;
};

const compileSampleHtml = (bodyOrFullHtml, useSampleData = true) => {
  if (!bodyOrFullHtml) return "<p>Preview not available</p>";

  const unbadgedHtml = convertBadgesToRawTags(bodyOrFullHtml);

  // Ensure editable body is wrapped with official Conserve email layout
  let fullHtml = unbadgedHtml;
  if (!fullHtml.includes("<!DOCTYPE") && !fullHtml.includes("<html")) {
    fullHtml = wrapWithConserveEmailLayout(unbadgedHtml, window.location.origin);
  } else {
    fullHtml = fullHtml.replace(/{{APP_URL}}/g, window.location.origin);
  }

  let rendered = fullHtml;
  if (useSampleData) {
    rendered = rendered.replace(
      /\{\{#each\s+_otp\}\}[\s\S]*?\{\{\/each\}\}/gi,
      `<span style="display:inline-block;border:2px solid #5786cc;width:64px;height:64px;text-align:center;line-height:60px;font-weight:700;border-radius:8px;font-size:48px;color:#5786cc;margin-right:6px;margin-bottom:8px;">1</span>` +
      `<span style="display:inline-block;border:2px solid #5786cc;width:64px;height:64px;text-align:center;line-height:60px;font-weight:700;border-radius:8px;font-size:48px;color:#5786cc;margin-right:6px;margin-bottom:8px;">2</span>` +
      `<span style="display:inline-block;border:2px solid #5786cc;width:64px;height:64px;text-align:center;line-height:60px;font-weight:700;border-radius:8px;font-size:48px;color:#5786cc;margin-right:6px;margin-bottom:8px;">3</span>` +
      `<span style="display:inline-block;border:2px solid #5786cc;width:64px;height:64px;text-align:center;line-height:60px;font-weight:700;border-radius:8px;font-size:48px;color:#5786cc;margin-right:6px;margin-bottom:8px;">4</span>`
    );
    Object.keys(sampleReplacements).forEach((key) => {
      const val = sampleReplacements[key];
      const regex = new RegExp(`{{${key}}}`, "g");
      rendered = rendered.replace(regex, val);
    });
    rendered = rendered.replace(/{{[^{}]+}}/g, "");
  } else {
    rendered = rendered.replace(/{{APP_URL}}/g, window.location.origin);
  }

  // Replace spacer gif images with vertical spacer so table cells don't collapse
  rendered = rendered.replace(
    /<img[^>]*spacer\.gif[^>]*>/gi,
    '<div style="height: 16px; line-height: 16px; font-size: 1px;">&nbsp;</div>'
  );
  return rendered;
};

const EditNotificationEmail = ({ templateId, onBack }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const [pendingNavUrl, setPendingNavUrl] = useState(null);

  const getLoggedInUser = () => {
    try {
      const stored = localStorage.getItem("LoginUserData");
      if (!stored) return null;
      return JSON.parse(stored);
    } catch {
      return null;
    }
  };

  const loginData = getLoggedInUser();
  const currentUserEmail =
    loginData?.userName?.email ||
    loginData?.userName?.user_email ||
    loginData?.user_email ||
    loginData?.email ||
    loginData?.data?.user_email ||
    loginData?.data?.email ||
    "";

  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);
  const [templateSubject, setTemplateSubject] = useState("");
  const [messageHtml, setMessageHtml] = useState("");
  const [initialSubject, setInitialSubject] = useState("");
  const [initialHtml, setInitialHtml] = useState("");
  const [showExitModal, setShowExitModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState("");
  const [saveErrorMsg, setSaveErrorMsg] = useState("");
  const [previewMode, setPreviewMode] = useState("sample"); // "sample" or "raw"

  // Test email state
  const [testTab, setTestTab] = useState("me"); // "me" or "other"
  const [testEmail, setTestEmail] = useState(currentUserEmail);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testSuccessMsg, setTestSuccessMsg] = useState("");
  const [testErrorMsg, setTestErrorMsg] = useState("");

  useEffect(() => {
    if (testTab === "me") {
      setTestEmail(currentUserEmail);
    }
  }, [currentUserEmail, testTab]);

  useEffect(() => {
    if (!templateId) return;
    setLoading(true);
    dispatch(GetNotificationEmailTemplateById(templateId))
      .then((res) => {
        setLoading(false);
        if (res?.data) {
          setTemplate(res.data);
          const subj = res.data.template_subject || "";
          setTemplateSubject(subj);
          setInitialSubject(subj);
          const origin = window.location.origin;
          const bodyHtml = extractEditableBody(res.data.template_html || "");
          const withOrigin = bodyHtml.replace(/{{APP_URL}}/g, origin);
          const badged = convertRawTagsToBadges(withOrigin);
          setMessageHtml(badged);
          setInitialHtml(badged);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, [templateId, dispatch]);

  const isDirty = Boolean(
    templateSubject !== initialSubject ||
    convertBadgesToRawTags(messageHtml).trim() !== convertBadgesToRawTags(initialHtml).trim()
  );

  // Intercept breadcrumb back and breadcrumb link clicks when dirty
  useEffect(() => {
    window.__conserveOnBreadcrumbBack = () => {
      if (isDirty) {
        setPendingNavUrl(null);
        setShowExitModal(true);
        return false;
      }
      return true;
    };

    window.__conserveOnBreadcrumbNavigate = (targetUrl) => {
      if (isDirty) {
        setPendingNavUrl(targetUrl || "/admin/notification-emails");
        setShowExitModal(true);
        return false;
      }
      return true;
    };

    return () => {
      delete window.__conserveOnBreadcrumbBack;
      delete window.__conserveOnBreadcrumbNavigate;
    };
  }, [isDirty]);

  // Warn before browser reload or tab close if changes are unsaved
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "Are you sure you want to exit? Your changes will be lost.";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const handleRequestExit = () => {
    if (isDirty) {
      setPendingNavUrl(null);
      setShowExitModal(true);
    } else {
      onBack();
    }
  };

  const handleInsertVariable = (varName) => {
    if (editorRef.current) {
      const quill = editorRef.current.getEditor();
      const range = quill.getSelection(true) || { index: quill.getLength() };
      quill.focus();
      quill.insertEmbed(range.index, "variable", varName, "user");
      quill.setSelection(range.index + 1, 0, "silent");
    } else {
      setMessageHtml((prev) => prev + `<span class="email-variable-badge" data-variable="${varName}" contenteditable="false">{{${varName}}}</span>`);
    }
  };

  const handleDragOver = (e) => {
    if (e.dataTransfer.types.includes("application/x-conserve-variable")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleDrop = (e) => {
    const varName = e.dataTransfer.getData("application/x-conserve-variable");
    if (!varName) return;
    e.preventDefault();
    if (editorRef.current) {
      const quill = editorRef.current.getEditor();
      let index = quill.getLength();
      if (document.caretRangeFromPoint) {
        const range = document.caretRangeFromPoint(e.clientX, e.clientY);
        if (range) {
          const blot = Quill.find(range.startContainer);
          if (blot) {
            const offset = blot.offset(quill.scroll);
            index = offset + (range.startOffset || 0);
          }
        }
      } else if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
        if (pos) {
          const blot = Quill.find(pos.offsetNode);
          if (blot) {
            const offset = blot.offset(quill.scroll);
            index = offset + pos.offset;
          }
        }
      }
      quill.focus();
      quill.insertEmbed(index, "variable", varName, "user");
      quill.setSelection(index + 1, 0, "silent");
    }
  };

  const handleSave = () => {
    setIsSaving(true);
    setSaveSuccessMsg("");
    setSaveErrorMsg("");

    const origin = window.location.origin;
    const originRegex = new RegExp(origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
    const unbadgedHtml = convertBadgesToRawTags(messageHtml);
    const cleanBody = unbadgedHtml.replace(originRegex, "{{APP_URL}}");
    const fullHtmlToSave = wrapWithConserveEmailLayout(cleanBody, "{{APP_URL}}");

    dispatch(
      UpdateNotificationEmailTemplate({
        notif_email_template_id: templateId,
        template_subject: templateSubject,
        template_html: fullHtmlToSave,
      })
    )
      .then((res) => {
        setIsSaving(false);
        if (res?.success || res?.status === 200) {
          const successMsg = "Template saved successfully!";
          setSaveSuccessMsg(successMsg);
          notify.show(successMsg, "success", 4000);
          setInitialSubject(templateSubject);
          setInitialHtml(messageHtml);
          setTimeout(() => setSaveSuccessMsg(""), 4000);
        } else {
          const errMsg = res?.message || "Failed to save template";
          setSaveErrorMsg(errMsg);
          notify.show(errMsg, "error", 4000);
        }
      })
      .catch((err) => {
        setIsSaving(false);
        const errMsg = err?.message || "Error saving template";
        setSaveErrorMsg(errMsg);
        notify.show(errMsg, "error", 4000);
      });
  };

  const handleSendTest = () => {
    if (!testEmail) {
      const errMsg = "Please enter a recipient email address.";
      setTestErrorMsg(errMsg);
      notify.show(errMsg, "error", 3000);
      return;
    }
    setIsSendingTest(true);
    setTestSuccessMsg("");
    setTestErrorMsg("");

    const origin = window.location.origin;
    const originRegex = new RegExp(origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
    const unbadgedHtml = convertBadgesToRawTags(messageHtml);
    const cleanBody = unbadgedHtml.replace(originRegex, "{{APP_URL}}");
    const fullHtmlToSend = wrapWithConserveEmailLayout(cleanBody, "{{APP_URL}}");

    dispatch(
      SendTestNotificationEmail({
        notif_email_template_id: templateId,
        recipient_email: testEmail,
        template_subject: templateSubject,
        template_html: fullHtmlToSend,
      })
    )
      .then((res) => {
        setIsSendingTest(false);
        if (res?.success || res?.status === 200) {
          const successMsg = `Test email sent to ${testEmail}!`;
          setTestSuccessMsg(successMsg);
          notify.show(successMsg, "success", 4000);
          setTimeout(() => setTestSuccessMsg(""), 4000);
        } else {
          const errMsg = res?.message || "Failed to send test email";
          setTestErrorMsg(errMsg);
          notify.show(errMsg, "error", 4000);
        }
      })
      .catch((err) => {
        setIsSendingTest(false);
        const errMsg = err?.message || "Error sending test email";
        setTestErrorMsg(errMsg);
        notify.show(errMsg, "error", 4000);
      });
  };

  if (loading) {
    return (
      <div className="p-16 flex justify-center items-center">
        <ThreeDotBouncing />
      </div>
    );
  }

  const availableVars = Array.isArray(template?.available_variables)
    ? template.available_variables
    : typeof template?.available_variables === "string"
    ? JSON.parse(template.available_variables || "[]")
    : [];

  return (
    <div className="w-full">
      <style>{`
        /* Visual Editor typography & spacing */
        .rich-text-editor .ql-container {
          font-family: 'Raleway', sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto;
          font-size: 14px;
        }
        .rich-text-editor .ql-editor {
          line-height: 1.6 !important;
          color: #344054 !important;
          min-height: 300px;
        }
        .rich-text-editor .ql-editor p {
          margin-top: 0 !important;
          margin-bottom: 14px !important;
          line-height: 1.6 !important;
        }
        .rich-text-editor .ql-editor p:empty,
        .rich-text-editor .ql-editor p > br:only-child {
          min-height: 14px !important;
          margin-bottom: 14px !important;
        }
        .rich-text-editor img[src*="spacer.gif"],
        .rich-text-editor img[src*="spacer"],
        .ql-editor img[src*="spacer.gif"],
        .ql-editor img[src*="spacer"] {
          display: none !important;
        }

        /* Non-editable atomic variable badges in editor */
        .email-variable-badge {
          display: inline-flex !important;
          align-items: center !important;
          background-color: #eff6ff !important;
          color: #1d4ed8 !important;
          border: 1px solid #bfdbfe !important;
          border-radius: 4px !important;
          padding: 1px 6px !important;
          margin: 0 2px !important;
          font-family: monospace, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          line-height: 1.4 !important;
          user-select: none !important;
          vertical-align: baseline !important;
          cursor: grab !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
        }
        .email-variable-badge:hover {
          background-color: #dbeafe !important;
          border-color: #93c5fd !important;
        }

        /* Live Preview container typography & spacing */
        .email-preview-container {
          font-family: 'Raleway', sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto;
          font-size: 14px;
          line-height: 1.6;
          color: #344054;
        }
        .email-preview-container p {
          margin-top: 0 !important;
          margin-bottom: 14px !important;
          line-height: 1.6 !important;
        }
        .email-preview-container p:empty,
        .email-preview-container p > br:only-child {
          min-height: 14px !important;
          margin-bottom: 14px !important;
        }
        .email-preview-container td {
          line-height: 1.6;
        }
        .email-preview-container img {
          max-width: 100%;
          height: auto;
        }
      `}</style>
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={handleRequestExit}
          type="button"
          className="text-gray-500 hover:text-gray-700 transition p-1 rounded-md hover:bg-gray-100"
          title="Back to templates"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M12.5 15L7.5 10L12.5 5"
              stroke="#344054"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Edit notification</h1>
      </div>

      {/* Two-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* Left Column: Edit Form */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          {/* Template Name */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Template name
            </label>
            <Input
              type="text"
              value={template?.template_name || ""}
              readOnly
              className="w-full bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700 cursor-not-allowed"
            />
          </div>

          {/* When this is sent */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              When this is sent
            </label>
            <p className="text-sm text-gray-600">
              {template?.trigger_description || "When this event is triggered by the system."}
            </p>
          </div>

          {/* Email Subject */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Email subject
            </label>
            <Input
              type="text"
              value={templateSubject}
              onChange={(e) => setTemplateSubject(e.target.value)}
              placeholder="Email subject..."
              className="w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Message Editor */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">
                Email Body
              </label>
              <span className="text-xs text-gray-500">
                Fixed header and footer are automatically applied
              </span>
            </div>

            <div
              className="border border-gray-300 rounded-lg overflow-hidden transition focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <RichTextEditor
                ref={editorRef}
                value={messageHtml}
                onChange={(content) => setMessageHtml(content)}
                placeholder="Write your email body..."
              />
            </div>
          </div>

          {/* Available Variables (Chips) */}
          {availableVars.length > 0 && (
            <div className="mb-6 p-3.5 bg-blue-50/50 border border-blue-100 rounded-lg">
              <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center justify-between">
                <span>Available variables (click or drag to insert):</span>
                <span className="text-[11px] text-gray-500 font-normal">
                  Injected automatically when sent
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
                {availableVars.map((varName) => (
                  <button
                    key={varName}
                    type="button"
                    draggable="true"
                    onDragStart={(e) => {
                      e.dataTransfer.setData("application/x-conserve-variable", varName);
                      e.dataTransfer.setData("text/plain", `{{${varName}}}`);
                      e.dataTransfer.effectAllowed = "copy";
                    }}
                    onClick={() => handleInsertVariable(varName)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-mono font-medium bg-white text-blue-700 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition cursor-grab active:cursor-grabbing shadow-sm select-none"
                    title="Click or drag into the email editor"
                  >
                    <svg className="w-2.5 h-2.5 text-blue-400 opacity-70" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 2a2 2 0 100 4 2 2 0 000-4zm6 0a2 2 0 100 4 2 2 0 000-4zM7 8a2 2 0 100 4 2 2 0 000-4zm6 0a2 2 0 100 4 2 2 0 000-4zM7 14a2 2 0 100 4 2 2 0 000-4zm6 0a2 2 0 100 4 2 2 0 000-4z" />
                    </svg>
                    &#123;&#123;{varName}&#125;&#125;
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Feedback messages */}
          {saveSuccessMsg && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 flex items-center gap-2">
              <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {saveSuccessMsg}
            </div>
          )}
          {saveErrorMsg && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {saveErrorMsg}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <BtnLight onClick={handleRequestExit} disabled={isSaving}>
              Cancel
            </BtnLight>
            <BtnPrimary onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save template"}
            </BtnPrimary>
          </div>
        </div>

        {/* Right Column: Live Preview & Test Email */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="border-b border-gray-200 pb-4 mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Email preview</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {previewMode === "sample"
                  ? "Sample data · Preview how the email looks with sample data."
                  : "Template tags · Preview with raw variable placeholders."}
              </p>
            </div>
            <div className="flex bg-gray-100 p-0.5 rounded-lg text-xs">
              <button
                type="button"
                onClick={() => setPreviewMode("sample")}
                className={`px-2.5 py-1 rounded-md transition font-medium ${
                  previewMode === "sample"
                    ? "bg-white text-gray-900 shadow-sm font-semibold"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                Sample data
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode("raw")}
                className={`px-2.5 py-1 rounded-md transition font-medium ${
                  previewMode === "raw"
                    ? "bg-white text-gray-900 shadow-sm font-semibold"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                Merge tags
              </button>
            </div>
          </div>

          {/* Email Preview Header */}
          <div className="bg-[#fcfcfd] border border-gray-200 rounded-xl p-3.5 mb-4 shadow-sm">
            <div className="flex items-center gap-2 pb-2.5 border-b border-gray-200/60 text-xs">
              <span className="w-16 shrink-0 text-xs font-semibold text-gray-400">To:</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white border border-gray-200 text-gray-800 font-medium text-xs shadow-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
                  <span>{previewMode === "sample" ? "Alex Example" : "{{recipient}}"}</span>
                  <span className="text-gray-400 font-normal">
                    {previewMode === "sample" ? "<alex@example.com>" : "<{{email_to}}>"}
                  </span>
                </span>
              </div>
            </div>

            <div className="flex items-start gap-2 pt-2.5 text-xs">
              <span className="w-16 shrink-0 text-xs font-semibold text-gray-400 pt-0.5">Subject:</span>
              <span className="text-sm font-semibold text-gray-900 leading-snug break-words flex-1">
                {previewMode === "sample"
                  ? compileSampleString(templateSubject)
                  : templateSubject}
              </span>
            </div>
          </div>

          {/* Rendered Preview Container */}
          <div className="border border-gray-200/90 rounded-xl p-4 sm:p-6 bg-[#f5faff] mb-6 max-h-[540px] overflow-y-auto shadow-inner">
            <div
              className="email-preview-container flex justify-center"
              dangerouslySetInnerHTML={{
                __html: compileSampleHtml(messageHtml, previewMode === "sample"),
              }}
            />
          </div>

          {/* Send a Test Email Section */}
          <div className="border-t border-gray-200 pt-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Send a test email</h3>

            {/* Test Tabs */}
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => {
                  setTestTab("me");
                  setTestEmail(currentUserEmail);
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  testTab === "me"
                    ? "bg-gray-100 text-gray-900 font-semibold"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Send to me
              </button>
              <button
                type="button"
                onClick={() => {
                  setTestTab("other");
                  setTestEmail("");
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  testTab === "other"
                    ? "bg-gray-100 text-gray-900 font-semibold"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Use another address
              </button>
            </div>

            {/* Recipient Input */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Test recipient email
              </label>
              <Input
                type="email"
                placeholder={testTab === "me" ? (currentUserEmail || "Your email address") : "name@example.com"}
                value={testEmail}
                readOnly={testTab === "me" && !!currentUserEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className={`w-full text-sm border border-gray-300 rounded-lg focus:outline-none ${
                  testTab === "me" && currentUserEmail
                    ? "bg-gray-50 text-gray-700 cursor-default"
                    : "bg-white text-gray-900 focus:ring-1 focus:ring-blue-500"
                }`}
              />
            </div>

            {/* Test Email Feedback */}
            {testSuccessMsg && (
              <div className="mb-3 p-2.5 rounded bg-green-50 border border-green-200 text-xs text-green-700">
                {testSuccessMsg}
              </div>
            )}
            {testErrorMsg && (
              <div className="mb-3 p-2.5 rounded bg-red-50 border border-red-200 text-xs text-red-700">
                {testErrorMsg}
              </div>
            )}

            {/* Send Test Button */}
            <BtnPrimary onClick={handleSendTest} disabled={isSendingTest}>
              {isSendingTest ? "Sending test email..." : "Send test email"}
            </BtnPrimary>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Confirmation Modal */}
      <Dialog
        isOpen={showExitModal}
        onClose={() => setShowExitModal(false)}
        onRequestClose={() => setShowExitModal(false)}
        bodyOpenClassName="overflow-hidden"
        className="customAlert"
        closable={false}
      >
        <div className="px-6 pt-6">
          <span className="w-12 h-12 rounded-full m-auto bg-warning-100 flex items-center justify-center">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                stroke="#B54708"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <p className="text-base text-gray-900 font-semibold text-center mt-4 mb-1">
            Are you sure you want to exit?
          </p>
          <p className="text-gray-600 text-center text-sm">
            Your changes will be lost if you leave without saving.
          </p>
        </div>
        <div className="mt-8 flex justify-between pb-6 px-6 gap-3">
          <BtnLight
            className="w-full"
            onClick={() => {
              setShowExitModal(false);
              setPendingNavUrl(null);
            }}
          >
            Keep editing
          </BtnLight>
          <BtnPrimary
            className="w-full bg-error-600 hover:bg-error-700 border-error-600 hover:border-error-700"
            onClick={() => {
              setShowExitModal(false);
              const target = pendingNavUrl;
              setPendingNavUrl(null);
              if (target) {
                navigate(target);
              } else {
                onBack();
              }
            }}
          >
            Exit without saving
          </BtnPrimary>
        </div>
      </Dialog>
    </div>
  );
};

export default EditNotificationEmail;
